// ReduxSlices/smtpSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";

// 1️⃣ Detect SMTP provider
export const checkSMTPProviderEmail = createAsyncThunk(
  "smtp/checkProvider",
  async ({ user_id, from_email }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const response = await axios.post(
        apiEndpoints.checkSMPTproviderEmail,
        { user_id, from_email },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// 2️⃣ Save SMTP configuration
export const saveSMTPConfig = createAsyncThunk(
  "smtp/saveConfig",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const response = await axios.post(apiEndpoints.saveSMTPConfig, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        (data &&
          (data.message || (typeof data === "string" ? data : undefined))) ||
        err.message ||
        "Failed to save SMTP config";
      return rejectWithValue({ status, message, data });
    }
  }
);

// 3️⃣ Verify SMTP configuration
export const verifySMTPConfig = createAsyncThunk(
  "smtp/verifyConfig",
  async ({ user_id, config_id }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const response = await axios.post(
        apiEndpoints.verifySMTPConfig,
        { user_id, config_id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Slice
const smtpSlice = createSlice({
  name: "smtp",
  initialState: {
    providerData: null,
    saveData: null,
    verifyData: null,
    loading: false,
    error: null,
  },
  reducers: {
    resetSMTPState: (state) => {
      state.providerData = null;
      state.saveData = null;
      state.verifyData = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check Provider
      .addCase(checkSMTPProviderEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.providerData = null;
      })
      .addCase(checkSMTPProviderEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.providerData = action.payload;
      })
      .addCase(checkSMTPProviderEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to detect SMTP provider";
      })

      // Save Config
      .addCase(saveSMTPConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.saveData = null;
      })
      .addCase(saveSMTPConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.saveData = action.payload;
      })
      .addCase(saveSMTPConfig.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to save SMTP config";
      })

      // Verify Config
      .addCase(verifySMTPConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verifyData = null;
      })
      .addCase(verifySMTPConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.verifyData = action.payload;
      })
      .addCase(verifySMTPConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to verify SMTP config";
      });
  },
});

export const { resetSMTPState } = smtpSlice.actions;
export default smtpSlice.reducer;
